import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  InputGroup,
  Spinner,
  Table,
  Pagination,
  Dropdown,
  Modal,
} from "react-bootstrap";
import PageHeader from "../components/shared/PageHeader";
import ImagePreviewModal from "../components/shared/ImagePreviewModal";
import PageContainer from "../components/shared/PageContainer";
import { formatDate, formatDateTime } from "../utils/formatters";
import { ordersApi, supplierApi } from "../services/services";
import { Order } from "../types";
import { useOrdersTableRefresh } from "../hooks/useOrdersTableRefresh";
import TableControls from "../components/shared/TableControls";
import { useRequiredAuth } from "../components/shared/AuthContext";
import {
  DeliveryNoteReceipt,
  ThermalPrintStyles,
  SupplierInfo as SupplierInfoType,
} from "../components/DeliveryNotePrint";

interface PrintReceiptItem {
  order: Order;
  formData: Record<string, any>;
}

const STATUS_OPTIONS = [
  { key: "Pending", text: "Pending", value: "Pending" },
  { key: "InProgress", text: "In Progress", value: "In Progress" },
  { key: "Completed", text: "Completed", value: "Completed" },
];

// Polling interval in milliseconds (30 seconds)
const POLLING_INTERVAL = 30000;

// Maps admin template display names to form schema slugs
const TEMPLATE_TO_SLUG: Record<string, string> = {
  "Celebration Cake": "celebration-cake",
  "Wedding Cake": "wedding-cake",
  "Daily Cake Order": "daily-order-cakes",
  "Standard": "standard-order",
};

const toDateInputValue = (value?: string | Date | null): string => {
  if (!value) return "";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  // Build the YYYY-MM-DD string from *local* date parts so it matches what
  // `<input type="date">` produces. Using `toISOString()` would convert to
  // UTC and could shift the day (e.g. a BST order on the 20th would compare
  // as the 19th and be filtered out).
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getStatusClasses = (status: string): string => {
  const normalized = status.toLowerCase();

  if (normalized.includes("complete")) {
    return "bg-success-subtle text-success-emphasis border-success-subtle";
  }

  if (normalized.includes("progress")) {
    return "bg-warning-subtle text-warning-emphasis border-warning-subtle";
  }

  return "bg-light text-body-secondary border-light";
};

const getFirstImage = (order: Order): string | null => {
  if (!order.formData) return null;
  try {
    const parsed = JSON.parse(order.formData);
    for (const key of ["picture1", "picture2", "image"]) {
      if (parsed[key] && typeof parsed[key] === "string" && parsed[key].startsWith("data:image")) {
        return parsed[key];
      }
    }
  } catch {}
  return null;
};

const getCustomerName = (order: Order): string | null => {
  if (!order.formData) return null;
  try {
    const parsed = JSON.parse(order.formData);
    if (typeof parsed.customerName === "string" && parsed.customerName.trim()) {
      return parsed.customerName.trim();
    }
  } catch {}
  return null;
};

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useRequiredAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [dateOrdered, setDateOrdered] = useState("");
  const [dateRequired, setDateRequired] = useState(() => toDateInputValue(new Date()));
  const [entriesPerPage, setEntriesPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [allowedTemplates, setAllowedTemplates] = useState<{ slug: string; label: string }[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [supplierInfo, setSupplierInfo] = useState<SupplierInfoType | null>(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState<PrintReceiptItem[]>([]);

  // Fetch supplier's allowed templates AND supplier info (used in print receipts)
  useEffect(() => {
    supplierApi.getMySupplier().then((supplier) => {
      if (supplier) {
        setSupplierInfo({
          businessDisplayName: supplier.businessDisplayName,
          businessAddress: supplier.businessAddress,
        });
      }
      if (supplier?.templateSelection) {
        const names = supplier.templateSelection.split(",").map((s: string) => s.trim()).filter(Boolean);
        const templates = names
          .map((name: string) => {
            const slug = TEMPLATE_TO_SLUG[name];
            return slug ? { slug, label: name } : null;
          })
          .filter(Boolean) as { slug: string; label: string }[];
        setAllowedTemplates(templates);
      }
    }).catch(() => {});
  }, []);

  const fetchOrders = useCallback(async () => {
    return await ordersApi.getOrders();
  }, []);

  const { refreshOrders, isRefreshing } = useOrdersTableRefresh<Order>(
    fetchOrders,
    setOrders,
    setLoading,
    () => {}
  );

  // Initial fetch + polling for updates (replaces SignalR)
  useEffect(() => {
    refreshOrders({ showLoading: true });

    // Poll for new orders periodically
    const interval = setInterval(() => {
      refreshOrders();
    }, POLLING_INTERVAL);

    const onSupplierChange = () => refreshOrders({ showLoading: true });
    window.addEventListener("mockSupplierChanged", onSupplierChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener("mockSupplierChanged", onSupplierChange);
    };
  }, [refreshOrders]);

  const handleStatusChange = (order: Order, newStatus: string) => {
    // Use functional update to avoid stale closure issues when changing multiple statuses
    setOrders((prev) =>
      prev.map((o) => (o.id === order.id ? { ...o, status: newStatus } : o))
    );

    ordersApi.updateOrderStatus(order.id, newStatus).catch(() => {
      // Revert by refetching from server on failure
      refreshOrders();
    });
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      !searchTerm ||
      order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.clientId?.toString().includes(searchTerm) ||
      getCustomerName(order)?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesDateOrdered = true;
    if (dateOrdered && order.dateOrdered) {
      matchesDateOrdered = toDateInputValue(order.dateOrdered) === dateOrdered;
    }

    let matchesDateRequired = true;
    if (dateRequired && order.dateRequired) {
      matchesDateRequired = toDateInputValue(order.dateRequired) === dateRequired;
    }

    return matchesSearch && matchesDateOrdered && matchesDateRequired;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateOrdered, dateRequired, entriesPerPage]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / entriesPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * entriesPerPage;
  const displayedOrders = filteredOrders.slice(startIndex, startIndex + entriesPerPage);
  const startEntry = filteredOrders.length === 0 ? 0 : startIndex + 1;
  const endEntry =
    filteredOrders.length === 0
      ? 0
      : Math.min(startIndex + displayedOrders.length, filteredOrders.length);

  const toggleSelected = (orderId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const allDisplayedSelected =
    displayedOrders.length > 0 &&
    displayedOrders.every((o) => selectedIds.has(o.id));
  const someDisplayedSelected =
    !allDisplayedSelected && displayedOrders.some((o) => selectedIds.has(o.id));

  const togglePageSelected = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allDisplayedSelected) {
        displayedOrders.forEach((o) => next.delete(o.id));
      } else {
        displayedOrders.forEach((o) => next.add(o.id));
      }
      return next;
    });
  };

  const handlePrintSelected = () => {
    if (selectedIds.size === 0) return;

    // Build receipt items from orders already in memory — no API calls needed.
    const data: PrintReceiptItem[] = orders
      .filter((o) => selectedIds.has(o.id))
      .map((order) => {
        let formData: Record<string, any> = {};
        if (order.formData) {
          try {
            formData = JSON.parse(order.formData);
          } catch {
            formData = {};
          }
        }
        return { order, formData };
      });

    setPrintData(data);
    setShowPrintModal(true);
  };

  const handleConfirmPrint = () => {
    window.print();
  };

  const handleClosePrintModal = () => {
    setShowPrintModal(false);
  };

  return (
    <PageContainer>
      <div className="d-flex flex-column gap-3">
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
          <PageHeader title="Manage Orders" subTitle="Orders" />

          <div className="ms-lg-auto d-flex gap-2">
            <Dropdown>
              <Dropdown.Toggle
                variant="primary"
                className="rounded-pill px-4 shadow-sm d-inline-flex align-items-center gap-2"
              >
                <i className="bi bi-plus-lg me-1"></i>
                Create Order
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {allowedTemplates.length > 0 ? (
                  allowedTemplates.map((t) => (
                    <Dropdown.Item
                      key={t.slug}
                      onClick={() => navigate(`/orders/create/${t.slug}`)}
                    >
                      {t.label}
                    </Dropdown.Item>
                  ))
                ) : (
                  <Dropdown.Item disabled>No templates assigned</Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
            <Button
              variant="success"
              onClick={handlePrintSelected}
              disabled={selectedIds.size === 0}
              className="rounded-pill px-4 shadow-sm d-inline-flex align-items-center gap-2"
            >
              <i className="bi bi-printer me-1"></i>
              Print Selected{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
            </Button>
            <Button
              variant="light"
              onClick={() => refreshOrders()}
              disabled={isRefreshing}
              className="border rounded-pill px-4 shadow-sm d-inline-flex align-items-center gap-2"
            >
              {isRefreshing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Refreshing...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh
                </>
              )}
            </Button>
          </div>

        </div>

        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-3 p-lg-4">
            <Row className="g-3 mb-3">
              <Col xs={12} md={6} xl={4}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="bi bi-calendar-event"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="date"
                    value={dateOrdered}
                    onChange={(e) => setDateOrdered(e.target.value)}
                    className="border-start-0"
                    aria-label="Date ordered"
                  />
                </InputGroup>
              </Col>
              <Col xs={12} md={6} xl={4}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="bi bi-calendar2-check"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="date"
                    value={dateRequired}
                    onChange={(e) => setDateRequired(e.target.value)}
                    className="border-start-0"
                    aria-label="Date required"
                  />
                </InputGroup>
              </Col>
            </Row>

            <TableControls
              entriesPerPage={entriesPerPage}
              onEntriesChange={setEntriesPerPage}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search orders..."
              showSearchIcon
            />

            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="small text-uppercase text-secondary">
                  <tr>
                    <th className="border-bottom pb-3 pt-2" style={{ width: "1%" }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        aria-label="Select all on page"
                        checked={allDisplayedSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someDisplayedSelected;
                        }}
                        onChange={togglePageSelected}
                        disabled={displayedOrders.length === 0}
                      />
                    </th>
                    <th className="border-bottom pb-3 pt-2">Order</th>
                    <th className="border-bottom pb-3 pt-2">Image</th>
                    <th className="border-bottom pb-3 pt-2">Branch</th>
                    <th className="border-bottom pb-3 pt-2">Customer</th>
                    <th className="border-bottom pb-3 pt-2">Date Ordered</th>
                    <th className="border-bottom pb-3 pt-2">Date Required</th>
                    <th className="border-bottom pb-3 pt-2">Status</th>
                    <th className="border-bottom pb-3 pt-2 text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-5">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading orders...
                      </td>
                    </tr>
                  ) : displayedOrders.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-5">
                        No orders found.
                      </td>
                    </tr>
                  ) : (
                    displayedOrders.map((order) => (
                      <tr key={order.id}>
                        <td className="py-4">
                          <Form.Check
                            type="checkbox"
                            aria-label={`Select order ${order.orderNumber || order.id}`}
                            checked={selectedIds.has(order.id)}
                            onChange={() => toggleSelected(order.id)}
                          />
                        </td>
                        <td className="py-4 fw-semibold text-dark">
                          {order.orderNumber || "-"}
                        </td>
                        <td className="py-4">
                          {getFirstImage(order) ? (
                            <a
                              href="#"
                              className="text-primary text-decoration-none small"
                              onClick={(e) => {
                                e.preventDefault();
                                setPreviewImage(getFirstImage(order));
                              }}
                            >
                              <i className="bi bi-image me-1"></i>View
                            </a>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="py-4">{order.clientName || "-"}</td>
                        <td className="py-4">{getCustomerName(order) || "-"}</td>
                        <td className="py-4 text-body-secondary">
                          {formatDateTime(order.dateOrdered)}
                        </td>
                        <td className="py-4 text-body-secondary">
                          {formatDate(order.dateRequired)}
                        </td>
                        <td className="py-4">
                          <Form.Select
                            size="sm"
                            value={order.status}
                            onChange={(e) => handleStatusChange(order, e.target.value)}
                            className={`w-auto rounded-pill fw-semibold border ${getStatusClasses(
                              order.status || ""
                            )}`}
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <option key={opt.key} value={opt.value}>
                                {opt.text}
                              </option>
                            ))}
                          </Form.Select>
                          {(order as any).isEdited && (
                            <span
                              className="badge rounded-pill bg-info-subtle text-info-emphasis border-info-subtle border px-2 py-1 ms-1"
                              title="This order has been edited by the client"
                            >
                              <i className="bi bi-pencil-fill me-1"></i>
                              Edited
                            </span>
                          )}
                        </td>
                        <td className="py-4 text-end">
                          <div className="d-flex gap-2 justify-content-end">
                            {order.status === "Pending" && (
                              <Button
                                variant="warning"
                                size="sm"
                                onClick={() => navigate(`/orders/view/${order.id}?edit=true`)}
                                className="border rounded-pill px-3 shadow-sm"
                              >
                                <i className="bi bi-pencil-square me-1"></i>
                                Edit
                              </Button>
                            )}
                            <Button
                              variant="light"
                              size="sm"
                              onClick={() => navigate(`/orders/view/${order.id}`)}
                              className="border rounded-pill px-3 shadow-sm"
                            >
                              <i className="bi bi-eye me-1"></i>
                              View
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 border-top mt-3 pt-3">
              <div className="small text-muted">
                Showing rows {startEntry} to {endEntry} of {filteredOrders.length}
              </div>

              <Pagination size="sm" className="mb-0 ms-md-auto">
                {Array.from({ length: totalPages }, (_, index) => {
                  const pageNumber = index + 1;
                  return (
                    <Pagination.Item
                      key={pageNumber}
                      active={pageNumber === safeCurrentPage}
                      onClick={() => setCurrentPage(pageNumber)}
                    >
                      {pageNumber}
                    </Pagination.Item>
                  );
                })}
              </Pagination>
            </div>
          </Card.Body>
        </Card>
      </div>
      <ImagePreviewModal
        show={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageSrc={previewImage || ""}
      />

      <Modal
        show={showPrintModal}
        onHide={handleClosePrintModal}
        fullscreen
        className="multi-print-modal"
      >
        <Modal.Header closeButton className="d-print-none">
          <Modal.Title>
            Print Preview ({printData.length}{" "}
            {printData.length === 1 ? "order" : "orders"})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 multi-print-modal-body">
          <ThermalPrintStyles />
          <style>{`
            @media print {
              /* Hide everything outside the modal */
              body > *:not(.modal):not(.modal-backdrop) {
                display: none !important;
              }
              body > .modal-backdrop {
                display: none !important;
              }
              /* Render the modal as a normal-flow full-width container */
              body > .modal {
                position: static !important;
                overflow: visible !important;
                background: transparent !important;
                display: block !important;
              }
              .modal-dialog {
                margin: 0 !important;
                max-width: none !important;
                width: auto !important;
                height: auto !important;
                transform: none !important;
              }
              .modal-content {
                border: none !important;
                box-shadow: none !important;
                background: white !important;
                height: auto !important;
              }
              .modal-header,
              .modal-footer {
                display: none !important;
              }
              .modal-body {
                padding: 0 !important;
                overflow: visible !important;
                height: auto !important;
              }
            }
          `}</style>
          {printData.map((item, idx) => (
            <DeliveryNoteReceipt
              key={item.order.id}
              order={item.order}
              formData={item.formData}
              supplierInfo={supplierInfo}
              fallbackSupplierName={user.supplierName}
              pageBreakBefore={idx > 0}
            />
          ))}
        </Modal.Body>
        <Modal.Footer className="d-print-none">
          <Button variant="secondary" onClick={handleClosePrintModal}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleConfirmPrint}>
            <i className="bi bi-printer me-1"></i>
            Print
          </Button>
        </Modal.Footer>
      </Modal>
    </PageContainer>
  );
};

export default Orders;
